import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Gift, Coffee, Crown, Cake, Award, ArrowRight, Star, Heart, Share2, 
  Trophy, Sparkles, Flame, Users, Clock, Zap, Target, TrendingUp,
  CheckCircle, Calendar, Bell, BellOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import AuthModal from '@/components/AuthModal';
import LoyaltySignupForm from '@/components/LoyaltySignupForm';
import { usePushNotifications, useNotificationPreferences } from '@/hooks/usePushNotifications';
import { api } from '@/utils/apiClient';
import { toast } from 'sonner';
import SEO, { SEO_CONFIG } from '@/components/SEO';

// Behavior-based reward definitions (matches backend)
const behaviorRewards = [
  {
    id: "first_purchase",
    icon: Gift,
    title: "First Purchase",
    description: "15% off your next order",
    trigger: "After your first order",
    color: "emerald"
  },
  {
    id: "streak_reward",
    icon: Flame,
    title: "Visit Streaks",
    description: "3 days = 3 off, 7 days = 20% off",
    trigger: "Visit consecutive days",
    color: "orange"
  },
  {
    id: "spend_credits",
    icon: TrendingUp,
    title: "Spend Credits",
    description: "5 for every 50 spent",
    trigger: "Automatic 10% return",
    color: "blue"
  },
  {
    id: "referral",
    icon: Users,
    title: "Referral Reward",
    description: "5 off for you AND your friend",
    trigger: "When friend orders",
    color: "purple"
  },
  {
    id: "birthday",
    icon: Cake,
    title: "Birthday Coffee",
    description: "FREE coffee on your birthday",
    trigger: "Your special day",
    color: "pink"
  },
  {
    id: "10_coffees",
    icon: Coffee,
    title: "10th Coffee Free",
    description: "Buy 9, get 1 FREE",
    trigger: "Every 10 coffees",
    color: "teal"
  }
];

// Coffee Pass perks
const coffeePassPerks = [
  { icon: Coffee, text: "1 FREE 8oz hot coffee daily (7-10 AM or 1-3:30 PM)" },
  { icon: Trophy, text: "365-day streak = $100 gift card" }
];

const RewardCard = ({ reward }) => {
  const Icon = reward.icon;
  const colorClasses = {
    emerald: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600",
    orange: "bg-orange-100 dark:bg-orange-900/30 text-orange-500",
    blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-500",
    purple: "bg-purple-100 dark:bg-purple-900/30 text-purple-500",
    pink: "bg-pink-100 dark:bg-pink-900/30 text-pink-500",
    teal: "bg-teal-100 dark:bg-teal-900/30 text-teal-500"
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-card p-6 rounded-2xl border border-border hover:border-primary/30 transition-all hover:shadow-lg"
      data-testid={`reward-card-${reward.id}`}
    >
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${colorClasses[reward.color]}`}>
        <Icon className="w-7 h-7" />
      </div>
      <h3 className="font-heading text-xl mb-2 text-foreground">{reward.title}</h3>
      <p className="text-lg font-medium text-primary mb-2">{reward.description}</p>
      <p className="text-sm text-muted-foreground">{reward.trigger}</p>
    </motion.div>
  );
};

export default function LoyaltyPage() {
  const { user, isAuthenticated } = useAuth();
  const [behaviorProfile, setBehaviorProfile] = useState(null);
  const [activeRewards, setActiveRewards] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralEmail, setReferralEmail] = useState('');
  const [referralName, setReferralName] = useState('');
  const [sendingReferral, setSendingReferral] = useState(false);
  
  // Push notifications
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
    permission: pushPermission
  } = usePushNotifications(user?.email);
  
  const { preferences: notifPrefs, updatePreferences } = useNotificationPreferences(user?.email);
  
  // CMS Content for hero section
  const [cmsHero, setCmsHero] = useState({
    tagline: "Rewards That Keep Giving",
    title: "Bulaccino Rewards",
    description: "Earn rewards with every visit. No complicated tiers - just simple, behavior-based perks that reward you automatically."
  });

  useEffect(() => {
    // Fetch CMS content for Loyalty page
    api.get('/simple-cms/pages/loyalty')
      .then(res => {
        const data = res.data;
        if (data.content) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data.content, 'text/html');
          const heroSection = doc.querySelector('.hero-section');
          if (heroSection) {
            setCmsHero({
              tagline: heroSection.querySelector('.tagline')?.textContent?.trim() || cmsHero.tagline,
              title: heroSection.querySelector('h1')?.textContent?.trim() || cmsHero.title,
              description: heroSection.querySelector('p:not(.tagline)')?.textContent?.trim() || cmsHero.description,
            });
          }
        }
      })
      .catch(err => console.log('Using default CMS content'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setLoading(true);
      // Fetch behavior profile
      Promise.all([
        api.get(`/ai-loyalty/profile/${encodeURIComponent(user.email)}`).then(res => res.data).catch(() => null),
        api.get(`/ai-loyalty/rewards/${encodeURIComponent(user.email)}`).then(res => res.data).catch(() => null)
      ])
        .then(([profile, rewards]) => {
          if (profile?.profile) setBehaviorProfile(profile.profile);
          if (rewards?.active) setActiveRewards(rewards.active);
          setLoading(false);
        })
        .catch(err => {
          console.error(err);
          setLoading(false);
        });
    }
  }, [isAuthenticated, user]);

  const handleShareProgress = async () => {
    if (!behaviorProfile) return;
    
    const shareText = `I've earned ${behaviorProfile.free_coffees_earned} free coffees at Bulaccino Cafe! Join me and start earning rewards.`;
    
    if (navigator.share) {
      await navigator.share({
        title: 'My Bulaccino Rewards',
        text: shareText,
        url: 'https://bulaccinocafe.com/loyalty'
      });
    } else {
      await navigator.clipboard.writeText(shareText + ' https://bulaccinocafe.com/loyalty');
      toast.success('Progress copied to clipboard!');
    }
  };

  const handleSendReferral = async () => {
    if (!referralEmail || !referralName) {
      toast.error('Please enter your friend\'s name and email');
      return;
    }
    
    setSendingReferral(true);
    try {
      const res = await api.post('/ai-loyalty/referral', {
        referrer_email: user.email,
        friend_email: referralEmail,
        friend_name: referralName
      });
      
      const data = res.data;
      if (data.success) {
        toast.success(`Referral invite sent to ${referralName}!`);
        setReferralEmail('');
        setReferralName('');
      } else {
        toast.error(data.error || 'Failed to send referral');
      }
    } catch (err) {
      toast.error('Failed to send referral');
    }
    setSendingReferral(false);
  };

  const coffeeProgress = behaviorProfile 
    ? ((behaviorProfile.coffee_count % 10) / 10) * 100
    : 0;

  const spendProgress = behaviorProfile
    ? ((behaviorProfile.total_spent % 50) / 50) * 100
    : 0;

  return (
    <div className="pt-24 pb-16 min-h-screen">
      <SEO {...SEO_CONFIG.loyalty} path="/loyalty" />
      
      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-secondary/50 to-transparent">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Award className="w-10 h-10 text-primary" />
          </motion.div>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-primary font-medium mb-4"
          >
            {cmsHero.tagline}
          </motion.p>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl md:text-6xl text-foreground mb-4"
          >
            {cmsHero.title}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-muted-foreground max-w-2xl mx-auto text-lg"
          >
            {cmsHero.description}
          </motion.p>
        </div>
      </section>

      {/* How Rewards Work - Behavior Based */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl mb-4">How Rewards Work</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Simple, automatic rewards based on your visits and purchases. No complicated points or tiers - just real value.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="behavior-rewards-grid">
            {behaviorRewards.map((reward) => (
              <RewardCard key={reward.id} reward={reward} />
            ))}
          </div>
        </div>
      </section>

      {/* Coffee Pass Upgrade */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="bg-card rounded-3xl p-8 md:p-12 border border-primary/20 shadow-xl">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Crown className="w-8 h-8 text-primary" />
                  <span className="text-sm font-medium text-primary uppercase tracking-wider">Premium Membership</span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl mb-4">Bula Coffee Pass</h2>
                <p className="text-muted-foreground mb-6">
                  For serious coffee lovers. Get daily free coffee plus exclusive perks that stack with your regular rewards.
                </p>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-4xl font-bold text-primary">$95</span>
                  <span className="text-muted-foreground">/year</span>
                </div>
                <Link to="/bula-coffee-club">
                  <Button className="rounded-full px-8" data-testid="coffee-pass-cta">
                    Learn More <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              
              <div className="space-y-4">
                {coffeePassPerks.map((perk, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-primary/5 rounded-xl">
                    <perk.icon className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{perk.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* User Status or Sign Up CTA */}
      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {isAuthenticated ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card p-8 rounded-3xl shadow-lg border border-border/50"
            >
              <div className="text-center mb-8">
                <h2 className="font-heading text-3xl mb-2">Welcome back, {user?.name?.split(' ')[0]}!</h2>
                <p className="text-muted-foreground">Here's your rewards dashboard</p>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : behaviorProfile ? (
                <div className="space-y-6">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-muted/50 rounded-2xl p-4 text-center">
                      <p className="text-3xl font-bold text-primary">{behaviorProfile.total_orders}</p>
                      <p className="text-sm text-muted-foreground">Total Orders</p>
                    </div>
                    <div className="bg-muted/50 rounded-2xl p-4 text-center">
                      <p className="text-3xl font-bold text-orange-500">{behaviorProfile.current_visit_streak}</p>
                      <p className="text-sm text-muted-foreground">Day Streak</p>
                    </div>
                    <div className="bg-muted/50 rounded-2xl p-4 text-center">
                      <p className="text-3xl font-bold text-teal-500">{behaviorProfile.coffee_count}</p>
                      <p className="text-sm text-muted-foreground">Coffees</p>
                    </div>
                    <div className="bg-muted/50 rounded-2xl p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-500">{behaviorProfile.free_coffees_earned}</p>
                      <p className="text-sm text-muted-foreground">Free Earned</p>
                    </div>
                  </div>

                  {/* Coffee Progress */}
                  <div className="bg-teal-50 dark:bg-teal-900/20 p-5 rounded-2xl border border-teal-200 dark:border-teal-800">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2">
                        <Coffee className="w-5 h-5 text-teal-500" />
                        <span className="font-medium">Free Coffee Progress</span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {behaviorProfile.coffee_count % 10} / 10 coffees
                      </span>
                    </div>
                    <Progress value={coffeeProgress} className="h-3 bg-teal-100 dark:bg-teal-900/50" />
                    {behaviorProfile.coffees_to_free === 0 ? (
                      <p className="text-sm text-teal-600 dark:text-teal-400 mt-2 font-medium">
                        You have a FREE coffee available!
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground mt-2">
                        {behaviorProfile.coffees_to_free} more coffees to your next free one
                      </p>
                    )}
                  </div>

                  {/* Spend Credits Progress */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-2xl border border-blue-200 dark:border-blue-800">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-blue-500" />
                        <span className="font-medium">Next 5 Credit</span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {(behaviorProfile.total_spent % 50).toFixed(2)} / 50 spent
                      </span>
                    </div>
                    <Progress value={spendProgress} className="h-3 bg-blue-100 dark:bg-blue-900/50" />
                    <p className="text-sm text-muted-foreground mt-2">
                      {(50 - (behaviorProfile.total_spent % 50)).toFixed(2)} more to earn 5 credit
                    </p>
                  </div>

                  {/* Streak Status */}
                  {behaviorProfile.current_visit_streak > 0 && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 p-5 rounded-2xl border border-orange-200 dark:border-orange-800">
                      <div className="flex items-center gap-2 mb-2">
                        <Flame className="w-5 h-5 text-orange-500" />
                        <span className="font-medium">Visit Streak: {behaviorProfile.current_visit_streak} days!</span>
                      </div>
                      {behaviorProfile.current_visit_streak >= 7 ? (
                        <p className="text-sm text-orange-600 dark:text-orange-400">
                          Amazing! You've unlocked 20% off your next order!
                        </p>
                      ) : behaviorProfile.current_visit_streak >= 3 ? (
                        <p className="text-sm text-orange-600 dark:text-orange-400">
                          Great job! You've earned 3 off. Keep going for 20% off at 7 days!
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {3 - behaviorProfile.current_visit_streak} more days to unlock 3 off!
                        </p>
                      )}
                    </div>
                  )}

                  {/* Active Rewards */}
                  {activeRewards.length > 0 && (
                    <div className="border border-primary/20 rounded-2xl p-5">
                      <h3 className="font-medium mb-4 flex items-center gap-2">
                        <Gift className="w-5 h-5 text-primary" />
                        Your Active Rewards
                      </h3>
                      <div className="space-y-3">
                        {activeRewards.map((reward) => (
                          <div key={reward.id} className="flex justify-between items-center p-3 bg-muted/50 rounded-xl">
                            <div>
                              <p className="font-medium">{reward.reward_name}</p>
                              <p className="text-xs text-muted-foreground">
                                Code: <span className="font-mono">{reward.code}</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-primary">
                                {reward.discount_percent ? `${reward.discount_percent}% OFF` : 
                                 reward.credit_amount ? `${reward.credit_amount} OFF` : 
                                 'FREE ITEM'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Expires {new Date(reward.expires_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Referral Section */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-5 rounded-2xl border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-2 mb-4">
                      <Users className="w-5 h-5 text-purple-500" />
                      <span className="font-medium">Refer a Friend - Both Get 5 Off!</span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Friend's name"
                        value={referralName}
                        onChange={(e) => setReferralName(e.target.value)}
                        className="px-4 py-2 rounded-lg border border-border bg-background"
                      />
                      <input
                        type="email"
                        placeholder="Friend's email"
                        value={referralEmail}
                        onChange={(e) => setReferralEmail(e.target.value)}
                        className="px-4 py-2 rounded-lg border border-border bg-background"
                      />
                    </div>
                    <Button 
                      onClick={handleSendReferral}
                      disabled={sendingReferral}
                      className="w-full mt-3 bg-purple-500 hover:bg-purple-600"
                    >
                      {sendingReferral ? 'Sending...' : 'Send Invite'}
                    </Button>
                  </div>

                  {/* Coffee Pass Status */}
                  {behaviorProfile.is_coffee_pass_member && (
                    <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-5 rounded-2xl border border-primary/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Crown className="w-5 h-5 text-primary" />
                        <span className="font-medium text-primary">Coffee Pass Member</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Expires: {new Date(behaviorProfile.coffee_pass_expiry).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Remember: FREE hot coffee daily 7-10 AM or 1-3:30 PM!
                      </p>
                    </div>
                  )}

                  {/* Push Notification Settings */}
                  {pushSupported && (
                    <div className="bg-slate-50 dark:bg-slate-900/50 p-5 rounded-2xl border border-slate-200 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          {pushSubscribed ? (
                            <Bell className="w-5 h-5 text-green-500" />
                          ) : (
                            <BellOff className="w-5 h-5 text-slate-400" />
                          )}
                          <span className="font-medium text-foreground">Reward Notifications</span>
                        </div>
                        <Button
                          variant={pushSubscribed ? "outline" : "default"}
                          size="sm"
                          className="rounded-full"
                          onClick={async () => {
                            if (pushSubscribed) {
                              const result = await unsubscribePush();
                              if (result.success) {
                                toast.success('Notifications disabled');
                              }
                            } else {
                              const result = await subscribePush();
                              if (result.success) {
                                toast.success('Notifications enabled! You\'ll be notified when you earn rewards.');
                              } else {
                                toast.error(result.error || 'Could not enable notifications');
                              }
                            }
                          }}
                        >
                          {pushSubscribed ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                      
                      {pushSubscribed && (
                        <div className="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                          <p className="text-xs text-muted-foreground mb-2">Notify me about:</p>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="flex items-center gap-2 text-sm text-foreground">
                              <input 
                                type="checkbox" 
                                checked={notifPrefs.reward_alerts}
                                onChange={(e) => updatePreferences({ reward_alerts: e.target.checked })}
                                className="rounded"
                              />
                              Reward Alerts
                            </label>
                            <label className="flex items-center gap-2 text-sm text-foreground">
                              <input 
                                type="checkbox" 
                                checked={notifPrefs.streak_reminders}
                                onChange={(e) => updatePreferences({ streak_reminders: e.target.checked })}
                                className="rounded"
                              />
                              Streak Reminders
                            </label>
                            <label className="flex items-center gap-2 text-sm text-foreground">
                              <input 
                                type="checkbox" 
                                checked={notifPrefs.win_back_offers}
                                onChange={(e) => updatePreferences({ win_back_offers: e.target.checked })}
                                className="rounded"
                              />
                              Special Offers
                            </label>
                            <label className="flex items-center gap-2 text-sm text-foreground">
                              <input 
                                type="checkbox" 
                                checked={notifPrefs.new_items}
                                onChange={(e) => updatePreferences({ new_items: e.target.checked })}
                                className="rounded"
                              />
                              New Items
                            </label>
                          </div>
                        </div>
                      )}
                      
                      {!pushSubscribed && (
                        <p className="text-xs text-muted-foreground">
                          Get notified when you earn rewards, complete streaks, or when we have special offers for you.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3 justify-center pt-4">
                    <Link to="/order" data-testid="loyalty-order-btn">
                      <Button className="rounded-full bg-primary hover:bg-primary/90 px-8">
                        Start Earning
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      className="rounded-full"
                      onClick={handleShareProgress}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share Progress
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">Start earning rewards by placing your first order!</p>
                  <Link to="/order">
                    <Button className="rounded-full">Order Now</Button>
                  </Link>
                </div>
              )}
            </motion.div>
          ) : (
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-8"
              >
                <Gift className="w-16 h-16 mx-auto mb-4 text-primary" />
                <h2 className="font-heading text-3xl md:text-4xl mb-4 text-foreground">
                  Join Bulaccino Rewards
                </h2>
                <p className="text-muted-foreground max-w-xl mx-auto">
                  Sign up to start earning rewards instantly. Get 15% off your next order 
                  and a free coffee on your birthday!
                </p>
              </motion.div>
              
              {/* Quick Benefits Preview */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-card p-4 rounded-xl text-center border border-border">
                  <Gift className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                  <p className="font-medium text-sm">15% Off First Order</p>
                </div>
                <div className="bg-card p-4 rounded-xl text-center border border-border">
                  <Coffee className="w-6 h-6 text-teal-500 mx-auto mb-2" />
                  <p className="font-medium text-sm">10th Coffee Free</p>
                </div>
                <div className="bg-card p-4 rounded-xl text-center border border-border">
                  <Cake className="w-6 h-6 text-pink-500 mx-auto mb-2" />
                  <p className="font-medium text-sm">Birthday Coffee</p>
                </div>
                <div className="bg-card p-4 rounded-xl text-center border border-border">
                  <Flame className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                  <p className="font-medium text-sm">Streak Rewards</p>
                </div>
                <div className="bg-card p-4 rounded-xl text-center border border-border">
                  <Users className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                  <p className="font-medium text-sm">Refer & Earn</p>
                </div>
                <div className="bg-card p-4 rounded-xl text-center border border-border">
                  <TrendingUp className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <p className="font-medium text-sm">5 per 50 Spent</p>
                </div>
              </div>
              
              <LoyaltySignupForm variant="default" />
            </div>
          )}
        </div>
      </section>

      {/* Win-Back Promise */}
      <section className="py-12 md:py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Heart className="w-12 h-12 text-pink-500 mx-auto mb-4" />
          <h2 className="font-heading text-3xl mb-4">We Miss You When You're Gone</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
            Haven't visited in a while? We'll send you a special welcome-back offer. 
            The longer it's been, the better the discount - up to 40% off to bring you back!
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <div className="bg-card px-4 py-2 rounded-full border border-border">
              <span className="text-muted-foreground">14 days away:</span>
              <span className="ml-2 font-medium text-pink-500">25% off</span>
            </div>
            <div className="bg-card px-4 py-2 rounded-full border border-border">
              <span className="text-muted-foreground">30 days away:</span>
              <span className="ml-2 font-medium text-pink-500">30% off</span>
            </div>
            <div className="bg-card px-4 py-2 rounded-full border border-border">
              <span className="text-muted-foreground">60+ days away:</span>
              <span className="ml-2 font-medium text-pink-500">40% off</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl text-center mb-8">Questions?</h2>
          
          <div className="space-y-4">
            <div className="bg-card p-6 rounded-2xl border border-border">
              <h3 className="font-medium mb-2 text-foreground">How do I earn rewards?</h3>
              <p className="text-muted-foreground text-sm">
                Rewards are automatic! Every visit, purchase, and milestone is tracked. You'll receive 
                notifications when you earn something new - no points to track or cards to carry.
              </p>
            </div>
            <div className="bg-card p-6 rounded-2xl border border-border">
              <h3 className="font-medium mb-2 text-foreground">How do visit streaks work?</h3>
              <p className="text-muted-foreground text-sm">
                Visit us on consecutive days and watch your streak grow! At 3 days, you get 3 off. 
                At 7 days, you unlock 20% off. Miss a day? No worries - just start a new streak.
              </p>
            </div>
            <div className="bg-card p-6 rounded-2xl border border-border">
              <h3 className="font-medium mb-2 text-foreground">What's the Bula Coffee Pass?</h3>
              <p className="text-muted-foreground text-sm">
                For $95/year, get 1 FREE 8oz hot coffee every day (7-10 AM or 1-3:30 PM), 
                and a chance at the $100 Jack's gift card if you complete a 365-day streak!
              </p>
            </div>
            <div className="bg-card p-6 rounded-2xl border border-border">
              <h3 className="font-medium mb-2 text-foreground">How do referrals work?</h3>
              <p className="text-muted-foreground text-sm">
                Enter your friend's name and email above to send them an invite. When they make their 
                first purchase, you BOTH get 5 off your next order. Win-win!
              </p>
            </div>
            <div className="bg-card p-6 rounded-2xl border border-border">
              <h3 className="font-medium mb-2 text-foreground">When can I claim my birthday coffee?</h3>
              <p className="text-muted-foreground text-sm">
                Your free birthday coffee is available for 7 days around your birthday. 
                Make sure your birthday is registered in your profile to unlock this treat!
              </p>
            </div>
          </div>
        </div>
      </section>

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </div>
  );
}
